// src/pages/materials/utils/materialsFormHelpers.js
import dayjs from "dayjs";

// --- helpers ---
export function toId(v) {
  if (v == null) return undefined;
  try { return typeof v === "string" ? v : String(v); } catch { return undefined; }
}

// "Модал (Modal) 25%, Вовна (Wool) 75%" -> [{name:'Модал (Modal)', percent:25}, ...]
export function parseComposition(str) {
  if (!str || typeof str !== "string") return [];
  return str
    .split(",")
    .map(s => s.trim())
    .map(chunk => {
      const m = chunk.match(/(.+?)\s*([0-9]+(?:[.,][0-9]+)?)\s*%$/u);
      if (!m) return null;
      const name = m[1].trim();
      const percent = Number(String(m[2]).replace(",", "."));
      if (!name || !Number.isFinite(percent)) return null;
      return { name, percent };
    })
    .filter(Boolean);
}

// ---- EAN validators ----
export function checksumEAN13(ean) {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const n = ean.charCodeAt(i) - 48;
    sum += (i % 2 === 0) ? n : n * 3;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === (ean.charCodeAt(12) - 48);
}

export function checksumEAN8(ean) {
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    const n = ean.charCodeAt(i) - 48;
    sum += (i % 2 === 0) ? n * 3 : n;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === (ean.charCodeAt(7) - 48);
}

export function eanValidator(_, value) {
  if (!value) return Promise.resolve();
  const v = String(value).trim();
  if (!/^\d+$/.test(v)) return Promise.reject(new Error("Лише цифри"));
  if (v.length === 13) {
    return checksumEAN13(v) ? Promise.resolve() : Promise.reject(new Error("Некоректний EAN-13 (контрольна сума)"));
  }
  if (v.length === 8) {
    return checksumEAN8(v) ? Promise.resolve() : Promise.reject(new Error("Некоректний EAN-8 (контрольна сума)"));
  }
  return Promise.reject(new Error("Підтримуються тільки 8 або 13 цифр"));
}

export function buildInitial(editing, parents, childrenByParent, suppliers, units) {
  if (!editing) return {};
  const initial = {
    // верх
    code: editing.code || "",
    name: editing.name || "",
    ean: editing.ean || "",

    // розділ/категорія
    section_id: toId(editing.section_id),
    category_id: toId(editing.category_id),

    // фото
    images: Array.isArray(editing.images) ? editing.images : [],

    // кольори/узори
    colors: Array.isArray(editing.colors) ? editing.colors : (editing.color ? [editing.color] : []),
    patterns: Array.isArray(editing.patterns) ? editing.patterns : [],
    color: editing.color || "",

    // інше
    composition: editing.composition || "",
    composition_parts: Array.isArray(editing.composition_parts)
      ? editing.composition_parts
      : parseComposition(editing.composition || ""),
    characteristics: editing.characteristics || "",

    supplier_id: toId(editing.supplier_id),
    unit_id: toId(editing.unit_id),
    supplier_name: editing.supplier_name || null,
    unit_name: editing.unit_name || null,

    quantity: editing.quantity ?? undefined,
    price: editing.price ?? undefined,

    purchased_at: editing.purchased_at ? dayjs(editing.purchased_at) : undefined,
    note: editing.note || "",
  };

  // Восстановление section/category по именам, если id отсутствуют
  if (!initial.section_id && (editing?.group || editing?.section_name) && Array.isArray(parents)) {
    const byName = parents.find(p => [editing?.group, editing?.section_name].filter(Boolean).includes(p.name));
    if (byName) initial.section_id = toId(byName._id || byName.id);
  }
  if (!initial.category_id && initial.section_id && editing?.category_name && childrenByParent) {
    const childs = childrenByParent[initial.section_id] || [];
    const found = childs.find(c => c.name === editing.category_name);
    if (found) initial.category_id = toId(found._id || found.id);
  }
  if (!initial.section_id && initial.category_id && childrenByParent) {
    const foundParent = Object.entries(childrenByParent).find(([, childs]) =>
      (childs || []).some(c => toId(c._id || c.id) === initial.category_id)
    );
    if (foundParent) initial.section_id = foundParent[0];
  }

  if (initial.supplier_id && !initial.supplier_name && Array.isArray(suppliers)) {
    const s = suppliers.find(x => toId(x._id || x.id) === initial.supplier_id);
    if (s) initial.supplier_name = s.name;
  }
  if (initial.unit_id && !initial.unit_name && Array.isArray(units)) {
    const u = units.find(x => toId(x._id || x.id) === initial.unit_id);
    if (u) initial.unit_name = u.name;
  }

  return initial;
}
